import { useNavigate, useParams } from "react-router-dom";
import { InstitutionWithAddresses } from "../../types/institution.types";
import { useEffect, useState } from "react";
import { InstitutionsAPI } from "../../APIs/institutions.api";
import { useInstitutions } from "../../context/InstitutionsContext";
import InstitutionsForm from "./institutions-form";
import { getObjectDiff } from "../../utils/object-diff.utils";
import { useToast } from "../../components/toast";

export function InstitutionsEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { institutions, getInstitutions, handleError } = useInstitutions();
  const [originalInstitution, setOriginalInstitution] =
    useState<InstitutionWithAddresses | null>(null);
  const [modifiedInstitution, setModifiedInstitution] =
    useState<InstitutionWithAddresses | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  useEffect(() => {
    const fetchInstitution = () => {
      const found = institutions.find(
        (inst) => inst.institution.id === Number(id)
      );

      if (found) {
        setOriginalInstitution(found);
        setModifiedInstitution(found);
      } else {
        handleError("Institución no encontrada", { persist: true });
        navigate("/dashboard/institutions");
        return;
      }
    };

    if (institutions.length === 0) {
      getInstitutions();
    } else {
      fetchInstitution();
    }
  }, [id, institutions, handleError, getInstitutions, navigate]);

  const handleInstitutionChange = (e: React.ChangeEvent<HTMLElement>) => {
    const { name, value } = e.target as HTMLInputElement;

    setModifiedInstitution((prevState) => {
      if (!prevState) return null;

      return {
        ...prevState,
        institution: {
          ...prevState.institution,
          [name]: value,
        },
      };
    });
  };
  const handleAddressChange = (e: React.ChangeEvent<HTMLElement>) => {
    const { name, value } = e.target as HTMLInputElement;
    const index = Number(e.target.id.split("-")[1]);

    setModifiedInstitution((prev) => {
      if (!prev) return null;

      return {
        ...prev,
        addresses: prev.addresses.map((address, i) =>
          i === index ? { ...address, [name]: value } : address
        ),
      };
    });
  };

  const removeAddress = (index: number) => {
    setModifiedInstitution((prev) => {
      if (!prev) return null;

      return {
        ...prev,
        addresses: prev.addresses.filter((_, i) => i !== index),
      };
    });
  };
  const handleCancel = () => {
    navigate("/dashboard/institutions", { replace: true });
  };

  const handleSubmit = async (data: InstitutionWithAddresses) => {
    setIsSubmitting(true);
    try {
      const changes = getObjectDiff(originalInstitution, modifiedInstitution);
      const { institution, ...rest } = changes;
      const { id, ...institutionWithoutId } = institution || {};

      const changesWithoutId = { ...rest, institution: institutionWithoutId };

      const response = await InstitutionsAPI.updateInstitution(
        data.institution.id,
        changesWithoutId
      );
      if (response.statusCode === 200) {
        getInstitutions();
        addToast({
          message: `¡Institución actualizada!`,
          type: "success",
          location: "bottom-center",
        });
        navigate("/dashboard/institutions");
      } else {
        handleError(response.message, { persist: true });
      }
    } catch (error) {
      handleError(error as string);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!modifiedInstitution) return null;

  return (
    <InstitutionsForm
      editingInstitution={modifiedInstitution}
      handleInstitutionChange={handleInstitutionChange}
      handleAddressChange={handleAddressChange}
      removeAddress={removeAddress}
      handleCancel={handleCancel}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
    />
  );
}
